/**
 * Проверка розыгрыша в браузере: загрузка пакета (с сервера или из файла), независимый пересчёт
 * всех пунктов, выгрузка CSV для сравнения котировок с внешними источниками.
 */
import { useEffect, useState } from 'react';
import { actionsCsv, aggregateCsv, componentsCsv, type SignedPackage, type VerifyReport } from '@mt/verification';
import { t } from '../i18n';
import { checkText, saveFile, sourceLabel, verify } from '../lib/verification';
import { Dialog } from './Dialog';

const ICON: Record<string, string> = { ok: '✓', fail: '✗', warn: '!', skip: '·' };

export function VerifyDialog({ initial, onClose }: { initial?: SignedPackage | null; onClose: () => void }) {
  const [signed, setSigned] = useState<SignedPackage | null>(initial ?? null);
  const [report, setReport] = useState<VerifyReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!signed) return;
    setBusy(true);
    setReport(null);
    verify(signed)
      .then(setReport)
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(false));
  }, [signed]);

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    try {
      setSigned(JSON.parse(await f.text()) as SignedPackage);
    } catch {
      setError(t('verify.notJson'));
    }
  };

  const pkg = signed?.package;
  const stem = pkg ? `market-table-${pkg.game.gameId}` : 'market-table';
  return (
    <Dialog title={t('verify.title')} onClose={onClose} wide>
      <div className="verify">
        <div className="verify-source">
          <label className="btn">
            {t('verify.chooseFile')}
            <input type="file" accept="application/json,.json" hidden onChange={(e) => void onFile(e.target.files?.[0])} />
          </label>
          <p className="muted-line">
            {t('verify.hintBefore')}
            <code>npm run verify</code>
            {t('verify.hintAfter')}
          </p>
        </div>
        {error && <p className="notice danger">{error}</p>}
        {pkg && (
          <dl className="verify-meta">
            <div>
              <dt>{t('verify.metaGame')}</dt>
              <dd className="mono">{pkg.game.gameId}</dd>
            </div>
            <div>
              <dt>{t('verify.metaComposition')}</dt>
              <dd>{pkg.index.components.map((c) => c.symbol).join(' · ')}</dd>
            </div>
            <div>
              <dt>{t('verify.metaP0')}</dt>
              <dd className="mono">{pkg.index.p0MarketTime.replace('T', ' ').slice(0, 16)}</dd>
            </div>
            <div>
              <dt>{t('verify.metaSource')}</dt>
              <dd title={pkg.game.sourceDescription}>{sourceLabel(pkg.game.source, pkg.game.sourceDescription)}</dd>
            </div>
          </dl>
        )}
        {busy && <p className="muted-line">{t('verify.busy')}</p>}
        {report && (
          <>
            <p className={`verify-summary ${report.ok ? 'ok' : 'fail'}`}>{checkText(report.summaryNote)}</p>
            <ol className="checks">
              {report.checks.map((c) => (
                <li key={c.id} className={c.status}>
                  <span className="check-icon" aria-label={c.status}>
                    {ICON[c.status]}
                  </span>
                  <div>
                    <b>{checkText({ code: `title.${c.id}` })}</b>
                    <ul>
                      {c.notes.map((n, i) => (
                        <li key={i}>{checkText(n)}</li>
                      ))}
                    </ul>
                  </div>
                </li>
              ))}
            </ol>
          </>
        )}
        {pkg && (
          <div className="verify-export">
            <span className="muted-line">{t('verify.exportHint')}</span>
            <div className="btn-row">
              <button className="btn tiny" onClick={() => saveFile(`${stem}-quotes.csv`, componentsCsv(pkg), 'text/csv')}>
                {t('verify.exportQuotes')}
              </button>
              <button className="btn tiny" onClick={() => saveFile(`${stem}-index.csv`, aggregateCsv(pkg), 'text/csv')}>
                {t('verify.exportIndex')}
              </button>
              <button className="btn tiny" onClick={() => saveFile(`${stem}-actions.csv`, actionsCsv(pkg), 'text/csv')}>
                {t('verify.exportActions')}
              </button>
              <button className="btn tiny" onClick={() => saveFile(`${stem}.json`, JSON.stringify(signed, null, 2), 'application/json')}>
                {t('verify.exportPackage')}
              </button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
